import { useEffect, useState } from "react";
import { Plus, Trash, PencilSimple, Copy, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CAMPAIGN_CHANNELS } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const KINDS = {
  empanelment_renewal: "Empanelment Renewal Outreach",
  new_branch_intro: "New Branch Introduction",
  festival_greeting: "Festival / Relationship Greeting",
  feedback_request: "Post-Job Feedback Request",
  reengage_dormant: "Re-engage Dormant Branch",
};
const EMPTY = { name: "", kind: "new_branch_intro", channel: "email", subject: "", body: "" };

export default function OutreachTemplates() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/coll/outreach_templates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/coll/outreach_templates/${editing}`, form);
      else await api.post("/coll/outreach_templates", form);
      toast.success(editing ? "Template updated" : "Template added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/coll/outreach_templates/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading templates…</div>;

  return (
    <div className="space-y-6" data-testid="outreach-templates-page">
      <PageHeader
        overline="AI Outreach" title="AI Message Templates"
        subtitle="Reusable, editable templates the AI personalises per recipient — use {{name}} and {{branch}} as placeholders"
        action={<Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="add-outreach-template-btn"><Plus size={16} className="mr-1" /> Add Template</Button>}
      />

      {items.length === 0 ? (
        <EmptyState icon={ChatText}>No templates yet.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-5 border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`otemplate-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display font-semibold text-lg text-primary truncate">{t.name}</h3>
                  <div className="text-xs text-accent mt-0.5">{KINDS[t.kind] || t.kind} · <span className="capitalize">{t.channel}</span></div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { navigator.clipboard.writeText(`${t.subject ? `Subject: ${t.subject}\n\n` : ""}${t.body}`); toast.success("Copied"); }} data-testid={`copy-otemplate-${t.id}`}><Copy size={14} /></Button>
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { setEditing(t.id); setForm({ ...EMPTY, ...t }); setOpen(true); }} data-testid={`edit-otemplate-${t.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => remove(t.id)} data-testid={`delete-otemplate-${t.id}`}><Trash size={14} /></Button>
                </div>
              </div>
              {t.subject && <div className="text-sm font-medium text-primary mt-3">Subject: {t.subject}</div>}
              <pre className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap font-sans line-clamp-6">{t.body}</pre>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Template" : "Add Message Template"}</DialogTitle>
            <DialogDescription>Use {"{{name}}"} and {"{{branch}}"} — they get replaced per recipient.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="outreach-template-form">
            <div className="space-y-1.5"><Label>Template Name *</Label><Input required value={form.name} onChange={set("name")} data-testid="ot-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Purpose</Label>
                <Select value={form.kind} onValueChange={set("kind")}>
                  <SelectTrigger data-testid="ot-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Channel</Label>
                <Select value={form.channel} onValueChange={set("channel")}>
                  <SelectTrigger data-testid="ot-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>{CAMPAIGN_CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Subject (email only)</Label><Input value={form.subject} onChange={set("subject")} data-testid="ot-subject" /></div>
            <div className="space-y-1.5"><Label>Body *</Label><Textarea required rows={8} value={form.body} onChange={set("body")} data-testid="ot-body" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-otemplate-btn">{editing ? "Update" : "Add"} Template</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
